import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useContractTypeService from '../../../app/services/hooks/useContractTypeService';
import useContractTemplateService from '../../../app/services/hooks/useContractTemplateService';
import useContractFieldService from '../../../app/services/hooks/useContractFieldService';
import useContractArticleService from '../../../app/services/hooks/useContractArticleService';
import useContractSectionService from '../../../app/services/hooks/useContractSectionService';
import useContractArticleOptionService from '../../../app/services/hooks/useContractArticleOptionService';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import ButtonTableAction from '../../../components/buttons/ButtonTableAction';
import InputField from '../../../components/InputField';
import TextAreaField from '../../../components/TextAreaField';
import SelectTwo from '../../../components/SelectTwo';
import FileUpload from '../../../components/FileUpload';
import { AppContext } from '../../../contexts/AppContext';

const ContractTypeConfig: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { editContractType, fetchingEdit } = useContractTypeService();
    const { getAllTemplates, storeTemplate, deleteTemplate, fetchingAll: fetchingTemplates, fetchingStore: fetchingStoreTemplate, fetchingDelete: fetchingDeleteTemplate } = useContractTemplateService();
    const { getAllFields, storeField, updateField, deleteField, fetchingAll: fetchingFields, fetchingStore: fetchingStoreField, fetchingUpdate: fetchingUpdateField, fetchingDelete: fetchingDeleteField } = useContractFieldService();
    const { getAllArticles, storeArticle, updateArticle, deleteArticle, fetchingAll: fetchingArticles, fetchingStore: fetchingStoreArticle, fetchingUpdate: fetchingUpdateArticle, fetchingDelete: fetchingDeleteArticle } = useContractArticleService();
    const { getAllSections, storeSection, editSection, updateSection, deleteSection, fetchingAll: fetchingSections, fetchingStore: fetchingStoreSection, fetchingEdit: fetchingEditSection, fetchingUpdate: fetchingUpdateSection, fetchingDelete: fetchingDeleteSection } = useContractSectionService();
    const { getAllOptions, storeOption, updateOption, deleteOption, fetchingAll: fetchingOptions, fetchingStore: fetchingStoreOption, fetchingUpdate: fetchingUpdateOption, fetchingDelete: fetchingDeleteOption } = useContractArticleOptionService();
    const { requestConfirmation } = useReactConfirmAlert();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    
    const [contractType, setContractType] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('templates');
    
    // Templates
    const [templates, setTemplates] = useState<any[]>([]);
    const [showTemplateForm, setShowTemplateForm] = useState(false);
    const [templateForm, setTemplateForm] = useState({ name: '', template_type: 'preview', file: null as File | null });
    
    // Sections
    const [sections, setSections] = useState<any[]>([]);
    const [showSectionForm, setShowSectionForm] = useState(false);
    const [editingSection, setEditingSection] = useState<any>(null);
    const [sectionForm, setSectionForm] = useState({ name: '', description: '', icon: '', order: 0 });
    
    // Fields
    const [fields, setFields] = useState<any[]>([]);
    const [showFieldForm, setShowFieldForm] = useState(false);
    const [editingField, setEditingField] = useState<any>(null);
    const [fieldForm, setFieldForm] = useState({
        label: '', placeholder_key: '', 
        options: [], default_value: '', is_required: true, order: 0, 
        section_id: null as number | null, help_text: ''
    });
    
    // Articles
    const [articles, setArticles] = useState<any[]>([]);
    const [showArticleForm, setShowArticleForm] = useState(false);
    const [editingArticle, setEditingArticle] = useState<any>(null);
    const [articleForm, setArticleForm] = useState({
        title: '', content: '', placeholder_in_template: '', is_optional: true, order: 0
    });
    
    // Article Options (Alternativas)
    const [expandedArticle, setExpandedArticle] = useState<number | null>(null);
    const [articleOptions, setArticleOptions] = useState<{[key: number]: any[]}>({});
    const [showOptionForm, setShowOptionForm] = useState<number | null>(null);
    const [editingOption, setEditingOption] = useState<any>(null);
    const [optionForm, setOptionForm] = useState({
        title: '', content: '', order: 0
    });

    useEffect(() => {
        loadContractType();
    }, []);

    useEffect(() => {
        if (contractType) {
            if (activeTab === 'templates') loadTemplates();
            if (activeTab === 'sections') loadSections();
            if (activeTab === 'fields') {
                loadFields();
                loadSections(); // Cargar secciones para el dropdown
            }
            if (activeTab === 'articles') loadArticles();
        }
    }, [activeTab, contractType]);

    useEffect(() => {
        const loading = fetchingEdit || fetchingTemplates || fetchingStoreTemplate || fetchingDeleteTemplate ||
                       fetchingFields || fetchingStoreField || fetchingUpdateField || fetchingDeleteField ||
                       fetchingArticles || fetchingStoreArticle || fetchingUpdateArticle || fetchingDeleteArticle;
        if (loading) {
            showLoading?.();
        } else {
            hideLoading?.();
        }
    }, [fetchingEdit, fetchingTemplates, fetchingStoreTemplate, fetchingDeleteTemplate,
        fetchingFields, fetchingStoreField, fetchingUpdateField, fetchingDeleteField,
        fetchingArticles, fetchingStoreArticle, fetchingUpdateArticle, fetchingDeleteArticle]);

    const loadContractType = () => {
        editContractType(parseInt(id), {
            onSuccess: (res: any) => setContractType(res.data.contract_type),
            onError: (res: any) => {
                toast.error(res.message);
                history.push('/intranet/contract-types');
            }
        });
    };

    const loadTemplates = () => {
        getAllTemplates(parseInt(id), {
            onSuccess: (res: any) => setTemplates(res.data.templates || []),
            onError: (res: any) => toast.error(res.message)
        });
    };

    const loadFields = () => {
        getAllFields(parseInt(id), {
            onSuccess: (res: any) => setFields(res.data.fields || []),
            onError: (res: any) => toast.error(res.message)
        });
    };

    const loadArticles = () => {
        getAllArticles(parseInt(id), {
            onSuccess: (res: any) => setArticles(res.data.articles || []),
            onError: (res: any) => toast.error(res.message)
        });
    };

    const loadSections = () => {
        getAllSections(parseInt(id), {
            onSuccess: (res: any) => setSections(res.data.sections || []),
            onError: (res: any) => toast.error(res.message)
        });
    };

    const handleStoreSection = () => {
        const data = {
            contract_type_id: parseInt(id),
            ...sectionForm
        };

        const action = editingSection 
            ? updateSection(editingSection.id, sectionForm, {
                onSuccess: (res: any) => {
                    toast.success(res.message);
                    resetSectionForm();
                    loadSections();
                },
                onError: (res: any) => toast.error(res.message),
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Corrige los errores');
                }
            })
            : storeSection(data, {
                onSuccess: (res: any) => {
                    toast.success(res.message);
                    resetSectionForm();
                    loadSections();
                },
                onError: (res: any) => toast.error(res.message),
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Corrige los errores');
                }
            });
    };

    const handleEditSection = (section: any) => {
        setEditingSection(section);
        setSectionForm({
            name: section.name,
            description: section.description || '',
            icon: section.icon || '',
            order: section.order
        });
        setShowSectionForm(true);
    };

    const handleDeleteSection = (sectionId: number, sectionName: string) => {
        requestConfirmation({
            title: '¿Eliminar sección?',
            message: `¿Está seguro que desea eliminar la sección "${sectionName}"?`,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        deleteSection(sectionId, {
                            onSuccess: (res: any) => {
                                toast.success(res.message);
                                loadSections();
                            },
                            onError: (res: any) => toast.error(res.message)
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => { /* Cancelar */ }
                }
            }
        });
    };

    const resetSectionForm = () => {
        setSectionForm({ name: '', description: '', icon: '', order: 0 });
        setShowSectionForm(false);
        setEditingSection(null);
        setErrors({});
    };

    // ========== TEMPLATES ==========
    const handleStoreTemplate = () => {
        if (!templateForm.file) {
            toast.error('Debes seleccionar un archivo Word');
            return;
        }
        
        const data = {
            contract_type_id: id,
            name: templateForm.name,
            template_type: templateForm.template_type,
            template_file: templateForm.file
        };
        
        storeTemplate(data, {
            onSuccess: (res: any) => {
                toast.success(res.message);
                setShowTemplateForm(false);
                setTemplateForm({ name: '', template_type: 'preview', file: null });
                loadTemplates();
            },
            onError: (res: any) => toast.error(res.message),
            onFieldError: (res: any) => {
                setErrors(res.data);
                toast.error('Corrige los errores');
            }
        });
    };

    const handleDeleteTemplate = (templateId: number, name: string) => {
        requestConfirmation({
            title: '¿Eliminar template?',
            message: `¿Eliminar "${name}"?`,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        deleteTemplate(templateId, {
                            onSuccess: (res: any) => {
                                toast.success(res.message);
                                loadTemplates();
                            },
                            onError: (res: any) => toast.error(res.message)
                        });
                    }
                },
                cancelButton: { 
                    label: 'Cancelar', 
                    onClick: () => {
                        // No hacer nada
                    }
                }
            }
        });
    };

    // ========== FIELDS ==========
    const handleStoreField = () => {
        const data = { ...fieldForm, contract_type_id: parseInt(id) };
        
        if (editingField) {
            updateField(editingField.id, data, {
                onSuccess: (res: any) => {
                    toast.success(res.message);
                    resetFieldForm();
                    loadFields();
                },
                onError: (res: any) => toast.error(res.message),
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Corrige los errores');
                }
            });
        } else {
            storeField(data, {
                onSuccess: (res: any) => {
                    toast.success(res.message);
                    resetFieldForm();
                    loadFields();
                },
                onError: (res: any) => toast.error(res.message),
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Corrige los errores');
                }
            });
        }
    };

    const handleEditField = (field: any) => {
        setEditingField(field);
        setFieldForm(field);
        setShowFieldForm(true);
    };

    const handleDeleteField = (fieldId: number, label: string) => {
        requestConfirmation({
            title: '¿Eliminar campo?',
            message: `¿Eliminar "${label}"?`,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        deleteField(fieldId, {
                            onSuccess: (res: any) => {
                                toast.success(res.message);
                                loadFields();
                            },
                            onError: (res: any) => toast.error(res.message)
                        });
                    }
                },
                cancelButton: { 
                    label: 'Cancelar', 
                    onClick: () => {
                        // No hacer nada
                    }
                }
            }
        });
    };

    const resetFieldForm = () => {
        setShowFieldForm(false);
        setEditingField(null);
        setFieldForm({
            label: '', placeholder_key: '',
            options: [], default_value: '', is_required: true, order: 0,
            section_id: null, help_text: ''
        });
        setErrors({});
    };

    // ========== ARTICLES ==========
    const handleStoreArticle = () => {
        const data = { ...articleForm, contract_type_id: parseInt(id) };
        
        if (editingArticle) {
            updateArticle(editingArticle.id, data, {
                onSuccess: (res: any) => {
                    toast.success(res.message);
                    resetArticleForm();
                    loadArticles();
                },
                onError: (res: any) => toast.error(res.message),
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Corrige los errores');
                }
            });
        } else {
            storeArticle(data, {
                onSuccess: (res: any) => {
                    toast.success(res.message);
                    resetArticleForm();
                    loadArticles();
                },
                onError: (res: any) => toast.error(res.message),
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Corrige los errores');
                }
            });
        }
    };

    const handleEditArticle = (article: any) => {
        setEditingArticle(article);
        setArticleForm(article);
        setShowArticleForm(true);
    };

    const handleDeleteArticle = (articleId: number, title: string) => {
        requestConfirmation({
            title: '¿Eliminar artículo?',
            message: `¿Eliminar "${title}"?`,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        deleteArticle(articleId, {
                            onSuccess: (res: any) => {
                                toast.success(res.message);
                                loadArticles();
                            },
                            onError: (res: any) => toast.error(res.message)
                        });
                    }
                },
                cancelButton: { 
                    label: 'Cancelar', 
                    onClick: () => {
                        // No hacer nada
                    }
                }
            }
        });
    };

    const resetArticleForm = () => {
        setShowArticleForm(false);
        setEditingArticle(null);
        setArticleForm({
            title: '', content: '', placeholder_in_template: '', is_optional: true, order: 0
        });
        setErrors({});
    };

    // Funciones para alternativas
    const loadArticleOptions = (articleId: number) => {
        getAllOptions(articleId, {
            onSuccess: (res: any) => {
                setArticleOptions(prev => ({ ...prev, [articleId]: res.data.options || [] }));
            },
            onError: (res: any) => toast.error(res.message)
        });
    };

    const toggleArticleExpand = (articleId: number) => {
        if (expandedArticle === articleId) {
            setExpandedArticle(null);
        } else {
            setExpandedArticle(articleId);
            if (!articleOptions[articleId]) {
                loadArticleOptions(articleId);
            }
        }
    };

    const handleStoreOption = (articleId: number) => {
        const data = {
            article_id: articleId,
            ...optionForm
        };

        const action = editingOption
            ? updateOption(editingOption.id, optionForm, {
                onSuccess: (res: any) => {
                    toast.success(res.message);
                    resetOptionForm();
                    loadArticleOptions(articleId);
                },
                onError: (res: any) => toast.error(res.message),
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Corrige los errores');
                }
            })
            : storeOption(data, {
                onSuccess: (res: any) => {
                    toast.success(res.message);
                    resetOptionForm();
                    loadArticleOptions(articleId);
                },
                onError: (res: any) => toast.error(res.message),
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Corrige los errores');
                }
            });
    };

    const handleEditOption = (option: any) => {
        setEditingOption(option);
        setOptionForm({
            title: option.title,
            content: option.content,
            order: option.order
        });
        setShowOptionForm(option.article_id);
    };

    const handleDeleteOption = (optionId: number, optionTitle: string, articleId: number) => {
        requestConfirmation({
            title: '¿Eliminar alternativa?',
            message: `¿Está seguro que desea eliminar "${optionTitle}"?`,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        deleteOption(optionId, {
                            onSuccess: (res: any) => {
                                toast.success(res.message);
                                loadArticleOptions(articleId);
                            },
                            onError: (res: any) => toast.error(res.message)
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => { /* Cancelar */ }
                }
            }
        });
    };

    const resetOptionForm = () => {
        setOptionForm({ title: '', content: '', order: 0 });
        setShowOptionForm(null);
        setEditingOption(null);
        setErrors({});
    };

    if (!contractType) return null;

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <h4 className="mb-1">⚙️ Configurar: {contractType.name}</h4>
                                    <p className="text-muted mb-0">Gestión de templates, campos y artículos</p>
                                </div>
                                <ButtonGeneral
                                    type="button"
                                    variant="outline"
                                    title="Volver"
                                    onClick={() => history.push('/intranet/contract-types')}
                                    icon="fa-arrow-left"
                                />
                            </div>

                            <ul className="nav nav-tabs mb-4">
                                <li className="nav-item">
                                    <button className={`nav-link ${activeTab === 'templates' ? 'active' : ''}`} onClick={() => setActiveTab('templates')}>
                                        <i className="fas fa-file-word me-2" />Templates Word
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button className={`nav-link ${activeTab === 'sections' ? 'active' : ''}`} onClick={() => setActiveTab('sections')}>
                                        📂 Secciones
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button className={`nav-link ${activeTab === 'fields' ? 'active' : ''}`} onClick={() => setActiveTab('fields')}>
                                        <i className="fas fa-list-alt me-2" />Campos Dinámicos
                                    </button>
                                </li>
                                {contractType.has_articles && (
                                    <li className="nav-item">
                                        <button className={`nav-link ${activeTab === 'articles' ? 'active' : ''}`} onClick={() => setActiveTab('articles')}>
                                            <i className="fas fa-paragraph me-2" />Cláusulas
                                        </button>
                                    </li>
                                )}
                            </ul>

                            {/* TAB: TEMPLATES */}
                            {activeTab === 'templates' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5><i className="fas fa-file-word me-2" />Templates Word</h5>
                                        <ButtonGeneral
                                            type="button"
                                            variant="primary"
                                            title="Subir Template"
                                            onClick={() => setShowTemplateForm(!showTemplateForm)}
                                            icon="fa-plus"
                                            size="small"
                                        />
                                    </div>

                                    {showTemplateForm && (
                                        <div className="card mb-4">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <InputField
                                                            label="Nombre del Template"
                                                            value={templateForm.name}
                                                            onChange={(value) => setTemplateForm({ ...templateForm, name: value })}
                                                            onFocus={() => onFocusRemove('name')}
                                                            error={fieldErrorMessage('name')}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <SelectTwo
                                                            label="Tipo de Template"
                                                            inputValue={templateForm.template_type}
                                                            onChange={(option: any) => setTemplateForm({ ...templateForm, template_type: option?.value })}
                                                            options={[
                                                                { value: 'preview', label: 'Preview (con marca de agua)' },
                                                                { value: 'final', label: 'Final (limpio)' }
                                                            ]}
                                                        />
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">Archivo Word (.docx)</label>
                                                        <FileUpload
                                                            onFileSelect={(file) => setTemplateForm({ ...templateForm, file })}
                                                            currentFile={templateForm.file}
                                                            acceptedTypes=".doc,.docx"
                                                            maxSize={10}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <ButtonGeneral
                                                        type="button"
                                                        variant="primary"
                                                        title="Guardar Template"
                                                        onClick={handleStoreTemplate}
                                                        loading={fetchingStoreTemplate}
                                                    />
                                                    <ButtonGeneral
                                                        type="button"
                                                        variant="outline"
                                                        title="Cancelar"
                                                        onClick={() => {
                                                            setShowTemplateForm(false);
                                                            setTemplateForm({ name: '', template_type: 'preview', file: null });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Nombre</th>
                                                    <th>Tipo</th>
                                                    <th>Placeholders</th>
                                                    <th className="text-center">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {templates.map((tmpl) => (
                                                    <tr key={tmpl.id}>
                                                        <td>{tmpl.name}</td>
                                                        <td><span className="badge bg-info">{tmpl.template_type_display}</span></td>
                                                        <td><small>{tmpl.extracted_placeholders.length} placeholders</small></td>
                                                        <td className="text-center">
                                                            <ButtonTableAction
                                                                callbackFunction={() => window.open(tmpl.template_file_url, '_blank')}
                                                                title="Descargar"
                                                                classIcon="fa-download"
                                                                colorIcon="text-info"
                                                                sizeIcon="16"
                                                            />
                                                            <ButtonTableAction
                                                                callbackFunction={() => handleDeleteTemplate(tmpl.id, tmpl.name)}
                                                                title="Eliminar"
                                                                classIcon="fa-trash"
                                                                colorIcon="text-danger"
                                                                sizeIcon="16"
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* TAB: SECTIONS */}
                            {activeTab === 'sections' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h5>📂 Secciones</h5>
                                            <p className="text-muted mb-0">Las secciones agrupan campos visualmente en el formulario del cliente</p>
                                        </div>
                                        <ButtonGeneral
                                            type="button"
                                            variant="primary"
                                            title="Nueva Sección"
                                            onClick={() => {
                                                resetSectionForm();
                                                setShowSectionForm(true);
                                            }}
                                            icon="fa-plus"
                                            size="small"
                                        />
                                    </div>

                                    {showSectionForm && (
                                        <div className="card mb-4">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <InputField
                                                            label="Nombre de la Sección"
                                                            value={sectionForm.name}
                                                            onChange={(value) => setSectionForm({ ...sectionForm, name: value })}
                                                            onFocus={() => onFocusRemove('name')}
                                                            error={fieldErrorMessage('name')}
                                                            placeholder="Ej: Datos del Empleador, Información Laboral"
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-md-3 mb-3">
                                                        <InputField
                                                            label="Ícono (FontAwesome)"
                                                            value={sectionForm.icon}
                                                            onChange={(value) => setSectionForm({ ...sectionForm, icon: value })}
                                                            placeholder="fa-user"
                                                        />
                                                    </div>
                                                    <div className="col-md-3 mb-3">
                                                        <InputField
                                                            label="Orden"
                                                            type="number"
                                                            value={sectionForm.order.toString()}
                                                            onChange={(value) => setSectionForm({ ...sectionForm, order: parseInt(value) || 0 })}
                                                        />
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <TextAreaField
                                                            label="Descripción"
                                                            value={sectionForm.description}
                                                            onChange={(value) => setSectionForm({ ...sectionForm, description: value })}
                                                            placeholder="Descripción breve de qué campos agrupa esta sección"
                                                            rows={2}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <ButtonGeneral
                                                        type="button"
                                                        variant="primary"
                                                        title={editingSection ? 'Actualizar' : 'Guardar'}
                                                        onClick={handleStoreSection}
                                                        loading={fetchingStoreSection || fetchingUpdateSection}
                                                    />
                                                    <ButtonGeneral
                                                        type="button"
                                                        variant="outline"
                                                        title="Cancelar"
                                                        onClick={resetSectionForm}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {sections.length === 0 ? (
                                        <div className="alert alert-info">
                                            No hay secciones creadas. Las secciones te permiten agrupar campos relacionados en el formulario del cliente.
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table">
                                                <thead>
                                                    <tr>
                                                        <th>Orden</th>
                                                        <th>Nombre</th>
                                                        <th>Descripción</th>
                                                        <th>Ícono</th>
                                                        <th className="text-center">Campos</th>
                                                        <th className="text-center">Acciones</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sections.map((section) => (
                                                        <tr key={section.id}>
                                                            <td>{section.order}</td>
                                                            <td><strong>{section.name}</strong></td>
                                                            <td>{section.description || '-'}</td>
                                                            <td>
                                                                {section.icon ? (
                                                                    <span title={section.icon}><i className={`fas ${section.icon}`}></i></span>
                                                                ) : '-'}
                                                            </td>
                                                            <td className="text-center">
                                                                <span className="badge bg-primary">{section.fields_count} campos</span>
                                                            </td>
                                                            <td className="text-center">
                                                                <ButtonTableAction
                                                                    callbackFunction={() => handleEditSection(section)}
                                                                    title="Editar"
                                                                    classIcon="fa-edit"
                                                                    colorIcon="text-warning"
                                                                    sizeIcon="16"
                                                                />
                                                                <ButtonTableAction
                                                                    callbackFunction={() => handleDeleteSection(section.id, section.name)}
                                                                    title="Eliminar"
                                                                    classIcon="fa-trash"
                                                                    colorIcon="text-danger"
                                                                    sizeIcon="16"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* TAB: FIELDS */}
                            {activeTab === 'fields' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5><i className="fas fa-list-alt me-2" />Campos Dinámicos</h5>
                                        <ButtonGeneral
                                            type="button"
                                            variant="primary"
                                            title="Nuevo Campo"
                                            onClick={() => {
                                                resetFieldForm();
                                                setShowFieldForm(true);
                                            }}
                                            icon="fa-plus"
                                            size="small"
                                        />
                                    </div>

                                    {showFieldForm && (
                                        <div className="card mb-4">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-12 mb-3">
                                                        <InputField
                                                            label="Etiqueta del Campo"
                                                            value={fieldForm.label}
                                                            onChange={(value) => setFieldForm({ ...fieldForm, label: value })}
                                                            onFocus={() => onFocusRemove('label')}
                                                            error={fieldErrorMessage('label')}
                                                            placeholder="Ej: Nombre del empleador"
                                                            required
                                                        />
                                                    </div>
                                                    
                                                    <div className="col-md-12 mb-3">
                                                        <InputField
                                                            label="Placeholder en Template"
                                                            value={fieldForm.placeholder_key}
                                                            onChange={(value) => setFieldForm({ ...fieldForm, placeholder_key: value })}
                                                            onFocus={() => onFocusRemove('placeholder_key')}
                                                            error={fieldErrorMessage('placeholder_key')}
                                                            placeholder="Nombre_Empleador (sin llaves)"
                                                            required
                                                        />
                                                        <small className="text-muted">El comodín que está en el Word (sin {'{{'} {'}}'} )</small>
                                                    </div>
                                                    
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Sección</label>
                                                        <SelectTwo
                                                            inputValue={fieldForm.section_id}
                                                            onChange={(option) => setFieldForm({ ...fieldForm, section_id: option?.value || null })}
                                                            options={sections.map(s => ({ value: s.id, label: s.name }))}
                                                            isClearable
                                                            placeholder="Seleccione una sección (opcional)"
                                                        />
                                                        <small className="text-muted">Primero crea secciones para agrupar campos</small>
                                                    </div>
                                                    
                                                    <div className="col-md-3 mb-3">
                                                        <InputField
                                                            label="Orden"
                                                            type="number"
                                                            value={fieldForm.order.toString()}
                                                            onChange={(value) => setFieldForm({ ...fieldForm, order: parseInt(value) || 0 })}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                    
                                                    <div className="col-md-3 mb-3">
                                                        <label className="form-label d-block">Requerido</label>
                                                        <div className="form-check form-switch mt-2">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id="field-required"
                                                                checked={fieldForm.is_required}
                                                                onChange={(e) => setFieldForm({ ...fieldForm, is_required: e.target.checked })}
                                                                style={{ width: '48px', height: '24px', cursor: 'pointer' }}
                                                            />
                                                            <label className="form-check-label ms-2" htmlFor="field-required" style={{ cursor: 'pointer' }}>
                                                                {fieldForm.is_required ? 'Sí' : 'No'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <ButtonGeneral
                                                        type="button"
                                                        variant="primary"
                                                        title={editingField ? 'Actualizar' : 'Guardar'}
                                                        onClick={handleStoreField}
                                                        loading={fetchingStoreField || fetchingUpdateField}
                                                    />
                                                    <ButtonGeneral
                                                        type="button"
                                                        variant="outline"
                                                        title="Cancelar"
                                                        onClick={resetFieldForm}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Orden</th>
                                                    <th>Etiqueta</th>
                                                    <th>Placeholder</th>
                                                    <th>Sección</th>
                                                    <th className="text-center">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fields.map((field) => (
                                                    <tr key={field.id}>
                                                        <td>{field.order}</td>
                                                        <td>{field.label}</td>
                                                        <td><code>{`{{${field.placeholder_key}}}`}</code></td>
                                                        <td>{field.section_name || '-'}</td>
                                                        <td className="text-center">
                                                            <ButtonTableAction
                                                                callbackFunction={() => handleEditField(field)}
                                                                title="Editar"
                                                                classIcon="fa-edit"
                                                                colorIcon="text-warning"
                                                                sizeIcon="16"
                                                            />
                                                            <ButtonTableAction
                                                                callbackFunction={() => handleDeleteField(field.id, field.label)}
                                                                title="Eliminar"
                                                                classIcon="fa-trash"
                                                                colorIcon="text-danger"
                                                                sizeIcon="16"
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* TAB: ARTICLES */}
                            {activeTab === 'articles' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h5><i className="fas fa-paragraph me-2" />Cláusulas</h5>
                                            <p className="text-muted mb-0">El cliente puede seleccionar estas cláusulas y elegir entre sus alternativas</p>
                                        </div>
                                        <ButtonGeneral
                                            type="button"
                                            variant="primary"
                                            title="Nueva Cláusula"
                                            onClick={() => {
                                                resetArticleForm();
                                                setShowArticleForm(true);
                                            }}
                                            icon="fa-plus"
                                            size="small"
                                        />
                                    </div>

                                    {showArticleForm && (
                                        <div className="card mb-4">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-8 mb-3">
                                                        <InputField
                                                            label="Título del Artículo"
                                                            value={articleForm.title}
                                                            onChange={(value) => setArticleForm({ ...articleForm, title: value })}
                                                            onFocus={() => onFocusRemove('title')}
                                                            error={fieldErrorMessage('title')}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <InputField
                                                            label="Orden"
                                                            type="number"
                                                            value={articleForm.order.toString()}
                                                            onChange={(value) => setArticleForm({ ...articleForm, order: parseInt(value) || 0 })}
                                                        />
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <TextAreaField
                                                            label="Contenido del Artículo"
                                                            value={articleForm.content}
                                                            onChange={(value) => setArticleForm({ ...articleForm, content: value })}
                                                            onFocus={() => onFocusRemove('content')}
                                                            error={fieldErrorMessage('content')}
                                                            rows={4}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <InputField
                                                            label="Placeholder en Template"
                                                            value={articleForm.placeholder_in_template}
                                                            onChange={(value) => setArticleForm({ ...articleForm, placeholder_in_template: value })}
                                                            onFocus={() => onFocusRemove('placeholder_in_template')}
                                                            error={fieldErrorMessage('placeholder_in_template')}
                                                            placeholder="articulo_confidencialidad"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <ButtonGeneral
                                                        type="button"
                                                        variant="primary"
                                                        title={editingArticle ? 'Actualizar' : 'Guardar'}
                                                        onClick={handleStoreArticle}
                                                        loading={fetchingStoreArticle || fetchingUpdateArticle}
                                                    />
                                                    <ButtonGeneral
                                                        type="button"
                                                        variant="outline"
                                                        title="Cancelar"
                                                        onClick={resetArticleForm}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {articles.length === 0 ? (
                                        <div className="alert alert-info">
                                            No hay cláusulas. Las cláusulas permiten al cliente elegir entre diferentes alternativas.
                                        </div>
                                    ) : (
                                        <div className="accordion" id="articlesAccordion">
                                            {articles.map((article) => (
                                                <div key={article.id} className="card mb-2">
                                                    <div className="card-header d-flex justify-content-between align-items-center" style={{ padding: '12px 16px' }}>
                                                        <div className="d-flex align-items-center flex-grow-1">
                                                            <button
                                                                className="btn btn-link text-decoration-none p-0 me-3"
                                                                onClick={() => toggleArticleExpand(article.id)}
                                                                style={{ fontSize: '18px' }}
                                                            >
                                                                <i className={`fas fa-chevron-${expandedArticle === article.id ? 'down' : 'right'}`}></i>
                                                            </button>
                                                            <div>
                                                                <strong>{article.title}</strong>
                                                                <br />
                                                                <small className="text-muted">{article.content.substring(0, 80)}...</small>
                                                                <br />
                                                                {/* Las alternativas se cargan al abrir la cláusula: antes de eso no hay cifra que mostrar */}
                                                                {articleOptions[article.id] ? (
                                                                    <span className="badge badge-soft-primary mt-1">
                                                                        {articleOptions[article.id].length} alternativas
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge badge-soft-secondary mt-1">Ver alternativas</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="d-flex gap-1">
                                                            <ButtonTableAction
                                                                callbackFunction={() => handleEditArticle(article)}
                                                                title="Editar"
                                                                classIcon="fa-edit"
                                                                colorIcon="text-warning"
                                                                sizeIcon="16"
                                                            />
                                                            <ButtonTableAction
                                                                callbackFunction={() => handleDeleteArticle(article.id, article.title)}
                                                                title="Eliminar"
                                                                classIcon="fa-trash"
                                                                colorIcon="text-danger"
                                                                sizeIcon="16"
                                                            />
                                                        </div>
                                                    </div>

                                                    {expandedArticle === article.id && (
                                                        <div className="card-body">
                                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                                <h6 className="mb-0">Alternativas de esta cláusula</h6>
                                                                <ButtonGeneral
                                                                    type="button"
                                                                    variant="primary"
                                                                    title="Nueva Alternativa"
                                                                    onClick={() => {
                                                                        resetOptionForm();
                                                                        setShowOptionForm(article.id);
                                                                    }}
                                                                    icon="fa-plus"
                                                                    size="small"
                                                                />
                                                            </div>

                                                            {showOptionForm === article.id && (
                                                                <div className="card mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                                                                    <div className="card-body">
                                                                        <div className="row">
                                                                            <div className="col-md-9 mb-3">
                                                                                <InputField
                                                                                    label="Título de la Alternativa"
                                                                                    value={optionForm.title}
                                                                                    onChange={(value) => setOptionForm({ ...optionForm, title: value })}
                                                                                    onFocus={() => onFocusRemove('title')}
                                                                                    error={fieldErrorMessage('title')}
                                                                                    placeholder="Ej: 1. Mutuo acuerdo de las partes"
                                                                                    required
                                                                                />
                                                                            </div>
                                                                            <div className="col-md-3 mb-3">
                                                                                <InputField
                                                                                    label="Orden"
                                                                                    type="number"
                                                                                    value={optionForm.order.toString()}
                                                                                    onChange={(value) => setOptionForm({ ...optionForm, order: parseInt(value) || 0 })}
                                                                                />
                                                                            </div>
                                                                            <div className="col-md-12 mb-3">
                                                                                <TextAreaField
                                                                                    label="Contenido"
                                                                                    value={optionForm.content}
                                                                                    onChange={(value) => setOptionForm({ ...optionForm, content: value })}
                                                                                    onFocus={() => onFocusRemove('content')}
                                                                                    error={fieldErrorMessage('content')}
                                                                                    rows={3}
                                                                                    required
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className="d-flex gap-2">
                                                                            <ButtonGeneral
                                                                                type="button"
                                                                                variant="primary"
                                                                                title={editingOption ? 'Actualizar' : 'Guardar'}
                                                                                onClick={() => handleStoreOption(article.id)}
                                                                                loading={fetchingStoreOption || fetchingUpdateOption}
                                                                                size="small"
                                                                            />
                                                                            <ButtonGeneral
                                                                                type="button"
                                                                                variant="outline"
                                                                                title="Cancelar"
                                                                                onClick={resetOptionForm}
                                                                                size="small"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {articleOptions[article.id]?.length === 0 || !articleOptions[article.id] ? (
                                                                <div className="alert alert-secondary">
                                                                    No hay alternativas para esta cláusula. Agrega al menos una.
                                                                </div>
                                                            ) : (
                                                                <table className="table table-sm">
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ width: '60px' }}>Orden</th>
                                                                            <th>Título</th>
                                                                            <th>Contenido</th>
                                                                            <th className="text-center" style={{ width: '120px' }}>Acciones</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {articleOptions[article.id]?.map((option) => (
                                                                            <tr key={option.id}>
                                                                                <td>{option.order}</td>
                                                                                <td><strong>{option.title}</strong></td>
                                                                                <td><small>{option.content.substring(0, 60)}...</small></td>
                                                                                <td className="text-center">
                                                                                    <ButtonTableAction
                                                                                        callbackFunction={() => handleEditOption(option)}
                                                                                        title="Editar"
                                                                                        classIcon="fa-edit"
                                                                                        colorIcon="text-warning"
                                                                                        sizeIcon="14"
                                                                                    />
                                                                                    <ButtonTableAction
                                                                                        callbackFunction={() => handleDeleteOption(option.id, option.title, article.id)}
                                                                                        title="Eliminar"
                                                                                        classIcon="fa-trash"
                                                                                        colorIcon="text-danger"
                                                                                        sizeIcon="14"
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContractTypeConfig;
